'use strict';

import React, { useRef, useCallback, useState, memo, useEffect } from 'react';

import Paper from '@mui/material/Paper';
import * as d3 from 'd3';
import IntervalLabel from 'js/components/Prescription/PrescriptionScale/IntervalLabel';
import useEvent from 'js/hooks/useEvent';
import { useLangFile } from 'js/context/LanguageContext';
import PropTypes from 'prop-types';
import useWindowDimensions from 'js/hooks/useWindowDimensions';

import style from 'js/components/Prescription/DraggableIntervals/PrescriptionScale/PrescriptionScale.module.less';
import Box from '@mui/material/Box';
import { isSurveyLayer } from 'js/components/Prescription/PrescriptionJob';
import { getLayerConfig } from '../../PrescriptionUtils';
import { renderCustomDemandLegend } from '../../../DemandScale/DemandScale';
import { SURVEY_LAYER_CLASSIFICATION_COLORS } from '../../../../constants/SurveyLayers';

const generateSurveyGradient = (context, width, intervals) => {
  const gradient = context.createLinearGradient(0, 0, width, 0);
  const steps = intervals.length;

  for (let i = 0; i < steps; i++) {
    const classification = intervals[i].classification;
    const pos = i > 0 ? i / steps : 0;
    const next = (i + 1) / steps;
    const col = SURVEY_LAYER_CLASSIFICATION_COLORS[classification];
    if (col) {
      gradient.addColorStop(pos, col);
      gradient.addColorStop(next, col);
    }
  }
  return gradient;
};

const padding = 12;

const PrescriptionClassificationScale = ({
  layer,
  intervals,
  overrideAreas,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
}) => {
  const LangFile = useLangFile();
  const dimensions = useWindowDimensions();
  const ctxRef = useRef(null);
  const canvasRef = useRef(null);
  const xMap = useRef(null);
  const forceRedraw = useState({});
  const layerConfig = getLayerConfig(layer);

  // Determine overridden area
  const overriddenArea = overrideAreas
    ? Object.values(overrideAreas).reduce((sum, cur) => sum + cur.area, 0)
    : 0;

  const overriddenAreaString = Number(overriddenArea).toFixed(2);

  // Handlers
  const generateSeparators = useCallback(() => {
    if (!intervals || !xMap.current) {
      return [];
    }

    return intervals
      .filter((interval, idx) => idx !== intervals.length - 1)
      .map((interval, idx) => {
        const nextInterval = intervals[idx + 1];
        const upperBound = nextInterval ? nextInterval.min : interval.max;

        const xPos = xMap.current(upperBound) - 2;

        return [
          <Box
            key={'handle ' + idx + 1}
            className={style.Handle}
            sx={{
              left: xPos,
            }}
          />,
        ];
      });
  }, [intervals, xMap]);

  const generateIntervalSizeLabels = useCallback(() => {
    if (!intervals || !xMap.current) {
      return null;
    }

    return intervals.map((interval, idx, array) => {
      const nextInterval = array[idx + 1];
      const lowerBound = interval.min;
      const upperBound = nextInterval ? nextInterval.min : interval.max;

      const xPos = lowerBound + (upperBound - lowerBound) / 2;

      let area = 0;

      if (isBulkPrescription && !appliedInitialIntervals) {
        Object.values(bulkPrescriptionJobs).forEach((job) => {
          const intervalForClassification = job.intervals.find(
            (jobInterval) => jobInterval.classification === interval.classification
          );

          if (intervalForClassification) {
            area = area + intervalForClassification.areaFinal;
          }
        });

        area = area.toFixed(2);
      } else {
        area = Number(interval.areaFinal).toFixed(2);
      }

      let icon = null;
      const classification = interval.classification;
      if (classification) {
        icon = renderCustomDemandLegend(classification, LangFile, layer);
      }

      return (
        <IntervalLabel key={idx} labelText={area + ' ha'} left={xMap.current(xPos)} top={'25px'}>
          {Boolean(icon) && <Box className={style.DemandIcon}>{icon}</Box>}
        </IntervalLabel>
      );
    });
  }, [
    intervals,
    layer,
    xMap,
    layerConfig,
    LangFile,
    isBulkPrescription,
    appliedInitialIntervals,
    bulkPrescriptionJobs,
  ]);

  const redrawCanvas = useCallback(() => {
    const context = ctxRef.current;
    const canvas = canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;

    context.clearRect(0, 0, width, height);

    if (isSurveyLayer(layer)) {
      context.fillStyle = generateSurveyGradient(context, width, intervals);
    } else {
      console.error('Unexpected source layer selected. Raw MEAN should only be possible.', layer);
    }
    context.fillRect(0, 0, width, height);
  }, [ctxRef, layer, intervals]);

  const updateDimensions = useCallback(() => {
    if (intervals) {
      const intervalValues =
        intervals.length > 1
          ? intervals.map((interval, index) => (index === 0 ? interval.min : interval.max))
          : [intervals[0].min, intervals[0].max];

      // Get the current height and width of the svg parent
      const currentWidth = canvasRef.current.offsetWidth;

      // Calculate the new mappings from domain to pixel space
      xMap.current = d3
        .scaleLinear()
        .domain(d3.extent(intervalValues, (d) => d))
        .range([0, currentWidth]);

      forceRedraw[1]({});
      redrawCanvas();
    }
  }, [canvasRef, intervals, redrawCanvas]);

  useEvent('resize', updateDimensions);

  useEffect(() => {
    if (canvasRef) {
      ctxRef.current = canvasRef.current.getContext('2d');
    }
  }, [canvasRef]);

  useEffect(() => {
    if (ctxRef.current) {
      updateDimensions();
    }
  }, [updateDimensions]);

  const canvasWidth = dimensions.width - 2 * padding;

  return (
    <Paper className={style.Root} elevation={0}>
      <div className={style.Spacer} />
      <div className={style.Title}>
        {isSurveyLayer(layer) &&
          LangFile.PrescriptionScale.distributionOfArea +
            layerConfig.getName(LangFile) +
            LangFile.PrescriptionScale.distributionOfArea2 +
            overriddenAreaString +
            LangFile.PrescriptionScale.distributionOfArea3}
      </div>
      <canvas width={canvasWidth} height={25} ref={canvasRef}></canvas>
      {generateSeparators()}
      {generateIntervalSizeLabels()}
    </Paper>
  );
};

PrescriptionClassificationScale.propTypes = {
  layer: PropTypes.string,
  intervals: PropTypes.array,
  overrideAreas: PropTypes.object,
  isBulkPrescription: PropTypes.bool,
  appliedInitialIntervals: PropTypes.array,
  bulkPrescriptionJobs: PropTypes.object,
};

export default memo(PrescriptionClassificationScale);
